import SimpleDrawer, {
  SimpleDrawerAction,
} from '../../shared/simple-drawer-new';
import SharedReviewLayoutNew, {
  ReviewSection,
} from '../../shared/shared-review-layout-new';
import SharedReviewLayoutMainCard, {
  MainSectionItem,
} from '../../shared/shared-review-layout-main-card';
import { ReviewSectionItem } from '../../shared/shared-review-layout-item';
import { Policy } from '../../../types';
import { useState } from 'react';
import dayjs from 'dayjs';
import { useQuery } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { getAllPoliciesQuery } from '../../api/users-api';
import { useUserSettings } from '../../utils/use-user-settings';
import SharedActionAccordion from '../../shared/shared-action-accordion';
import { policyActions } from '../../shared/policy-actions';
import AvatarChip from '../../shared/avatar-chip';
import { policyNarratives } from './policy-review-narratives';
import { formatPolicyScheduleTimes, transformActionHash } from './policy-utils';
import {
  ToolsRoles,
  isAbleToSeeUIElement,
} from '../../utils/roles/client-roles';
import { usePolicyActions } from './use-policy-actions';
import DeletePoliciesDialog from './delete-policies-dialog';
import RunPoliciesDialog from './run-policies-dialog';
import { Messages } from '../../utils/message-constants';

import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Edit from '@mui/icons-material/Edit';
import Delete from '@mui/icons-material/Delete';
import PlayArrow from '@mui/icons-material/PlayArrow';
import LibraryAdd from '@mui/icons-material/LibraryAdd';
import Update from '@mui/icons-material/Update';
import Check from '@mui/icons-material/Check';

interface PolicyDrawerContentProps {
  selectedPolicy: Policy;
  apiHost: string;
}

function PolicyDrawerContent({
  selectedPolicy,
  apiHost,
}: Readonly<PolicyDrawerContentProps>) {
  const [showAllTargets, setShowAllTargets] = useState(false);
  const [showAllEmails, setShowAllEmails] = useState(false);

  const mapSourceTypeToAvatarType = (
    sourceType: string | undefined
  ): 'User' | 'Group' | 'OrgUnit' | 'Domain' | 'gPanel Role' | 'Unknown' => {
    switch (sourceType) {
      case 'All_Users':
      case 'User':
        return 'User';
      case 'All_Groups':
      case 'Group':
        return 'Group';
      case 'OrgUnit':
        return 'OrgUnit';
      case 'Domain':
        return 'Domain';
      case 'gPanel_Role':
        return 'gPanel Role';
      case 'Mobile_Device':
      default:
        return 'Unknown';
    }
  };

  const getActionData = () =>
    selectedPolicy.actionHash
      ? transformActionHash(selectedPolicy.actionHash)
      : [];

  const createDetailNarrative = (action: any) => {
    if (action.type === 'Create_Gmail_Filter') {
      return 'Gmail filter will be created with the following criteria and actions:';
    }

    const narrativeFn = policyNarratives[action.type];
    return typeof narrativeFn === 'function'
      ? narrativeFn(action, selectedPolicy)
      : narrativeFn;
  };

  const mainSection: MainSectionItem = {
    mainTitle: selectedPolicy.policyName || 'N/A',
    description: selectedPolicy.description || 'None Provided',
    fields: [
      {
        label: 'Last Run',
        value: selectedPolicy.lastRunDate
          ? new Date(selectedPolicy.lastRunDate).toLocaleString()
          : 'Never',
        icon: Update,
      },
      {
        label: 'Created Date',
        value: selectedPolicy.createdDate
          ? dayjs(selectedPolicy.createdDate).format('MMM D, YYYY, h:mm A')
          : 'N/A',
        icon: Check,
      },
    ],
  };

  const targetsItems: ReviewSectionItem[] = [];
  if (selectedPolicy.policyType !== 'newUser') {
    targetsItems.push({
      label: 'Target Type:',
      value: selectedPolicy.sourceType || 'N/A',
    });

    targetsItems.push({
      label: 'Target(s):',
      value:
        selectedPolicy.allSource === false &&
        selectedPolicy.sources &&
        selectedPolicy.sources.length > 0 ? (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 1,
              width: '100%',
            }}
          >
            <Stack direction="column" gap={1}>
              {(showAllTargets
                ? selectedPolicy.sources
                : selectedPolicy.sources.slice(0, 3)
              ).map((source, index) => (
                <Stack
                  key={`${source}-${index}`}
                  direction="row"
                  gap={1}
                  alignItems="center"
                >
                  <AvatarChip
                    label={source}
                    email={source}
                    type={mapSourceTypeToAvatarType(selectedPolicy.sourceType)}
                    size="small"
                  />
                  {!showAllTargets &&
                    index === 2 &&
                    selectedPolicy.sources.length > 3 && (
                      <Chip
                        label={`+${selectedPolicy.sources.length - 3}`}
                        size="small"
                        variant="outlined"
                        onClick={() => setShowAllTargets(true)}
                        sx={{ cursor: 'pointer' }}
                      />
                    )}
                </Stack>
              ))}
            </Stack>
          </Box>
        ) : selectedPolicy.allSource ? (
          'All'
        ) : (
          'N/A'
        ),
    });
  }

  const scheduleItems: ReviewSectionItem[] = [];
  if (selectedPolicy.policyType === 'standard') {
    if (selectedPolicy.useRunDate && selectedPolicy.runDate) {
      scheduleItems.push({
        label: 'Run Date',
        value: dayjs(selectedPolicy.runDate).format('MMM D, YYYY, h:mm A'),
      });
    } else if (selectedPolicy.scheduled) {
      const times = selectedPolicy.times || [];
      const dates = selectedPolicy.dates || [];

      scheduleItems.push({
        label: 'Times',
        value: formatPolicyScheduleTimes(times).join(', ') || 'N/A',
      });

      scheduleItems.push({
        label: 'Dates',
        value: dates.join(', ') || 'N/A',
      });

      if (selectedPolicy.scheduleEndDate) {
        scheduleItems.push({
          label: 'Expiration Date',
          value:
            dayjs(selectedPolicy.scheduleEndDate).format('MM/DD/YYYY') || 'N/A',
        });
      }
    }
  }

  const actionData = getActionData();
  const actionItems: ReviewSectionItem[] = [];
  if (actionData && actionData.length > 0) {
    actionItems.push({
      value: (
        <Stack spacing={2} sx={{ width: '100%' }}>
          {actionData.map((action, idx) => {
            const actionOption = policyActions.find(
              (x) => x.value === action.type
            );
            if (!actionOption) return null;
            return (
              <SharedActionAccordion
                key={action.type + idx}
                id={action.type + idx}
                title={actionOption.label || action.type}
                description={createDetailNarrative(action)}
                type={actionOption.groupType}
                expanded={true}
                onToggleExpand={() => {}}
              />
            );
          })}
        </Stack>
      ),
      flex: true,
    });
  }

  const leftSections: ReviewSection[] = [
    {
      title: 'Basic Information',
      leftWidth: {
        labelWidth: 5,
        valueWidth: 7,
      },
      leftItems: [
        {
          label: 'Created By:',
          value: selectedPolicy.createdBy ? (
            <AvatarChip
              email={selectedPolicy.createdBy}
              type="User"
              label={selectedPolicy.createdBy}
            />
          ) : (
            'N/A'
          ),
          isVisible: !!selectedPolicy.createdBy,
        },
        {
          label: 'Policy Type:',
          value:
            selectedPolicy.policyType === 'standard'
              ? 'Standard'
              : 'New User Created',
        },
        {
          label: 'Manual Run:',
          value: Boolean(selectedPolicy.manualRun),
        },
        {
          label: 'Recurring Schedule:',
          value: Boolean(selectedPolicy.scheduled),
        },
        {
          label: 'Set Run Date:',
          value: Boolean(selectedPolicy.useRunDate),
        },
        {
          label: 'Policy ID:',
          value: selectedPolicy.id?.toString() || 'N/A',
        },
        {
          label: 'API Endpoint:',
          value: apiHost
            ? selectedPolicy.policyType === 'standard'
              ? apiHost + '/v1/policy'
              : apiHost + '/v1/policy/onboarding'
            : 'N/A',
          isVisible: !!apiHost,
        },
        {
          label: 'Emails to Notify:',
          value:
            selectedPolicy.notifyViaEmail &&
            selectedPolicy.emailsToNotify &&
            selectedPolicy.emailsToNotify.length > 0 ? (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 1,
                  width: '100%',
                }}
              >
                <Stack direction="column" gap={1}>
                  {(showAllEmails
                    ? selectedPolicy.emailsToNotify!
                    : selectedPolicy.emailsToNotify!.slice(0, 3)
                  ).map((email, index) => (
                    <Stack
                      key={`${email}-${index}`}
                      direction="row"
                      gap={1}
                      alignItems="center"
                    >
                      <AvatarChip
                        email={email}
                        label={email}
                        type="User"
                        size="small"
                      />
                      {!showAllEmails &&
                        index === 2 &&
                        selectedPolicy.emailsToNotify!.length > 3 && (
                          <Chip
                            label={`+${
                              selectedPolicy.emailsToNotify!.length - 3
                            }`}
                            size="small"
                            variant="outlined"
                            onClick={() => setShowAllEmails(true)}
                            sx={{ cursor: 'pointer' }}
                          />
                        )}
                    </Stack>
                  ))}
                </Stack>
              </Box>
            ) : (
              'N/A'
            ),
          isVisible: Boolean(
            selectedPolicy.notifyViaEmail &&
              selectedPolicy.emailsToNotify &&
              selectedPolicy.emailsToNotify.length > 0
          ),
        },
      ],
    },
  ];

  if (selectedPolicy.policyType !== 'newUser' && targetsItems.length > 0) {
    leftSections.push({
      title: 'Targets',
      leftWidth: {
        labelWidth: 5,
        valueWidth: 7,
      },
      leftItems: targetsItems,
    });
  }

  if (scheduleItems.length > 0) {
    leftSections.push({
      title: selectedPolicy.useRunDate ? 'Set Run Date' : 'Recurring Schedule',
      leftWidth: {
        labelWidth: 5,
        valueWidth: 7,
      },
      leftItems: scheduleItems,
    });
  }

  if (actionItems.length > 0) {
    leftSections.push({
      title: 'Actions',
      leftWidth: {
        labelWidth: 12,
        valueWidth: 12,
      },
      leftItems: actionItems,
    });
  }

  return (
    <Stack spacing={2} sx={{ width: '100%' }}>
      <SharedReviewLayoutMainCard section={mainSection} isDrawer={true} />
      <SharedReviewLayoutNew leftSections={leftSections} isDrawer={true} />
    </Stack>
  );
}

/**
 * Global, self-contained policy detail drawer.
 * Driven entirely by the `?selectedPolicy=<id>` URL search param.
 * Place this once at the App level — no props required.
 */
export default function PolicyDetailsDrawer() {
  const [searchParams] = useSearchParams();
  const { toolsRoles, changeLogRoles, apiUrl } = useUserSettings();
  const { closeDrawer, handlePolicyEdit, handlePolicyClone } =
    usePolicyActions();

  const selectedPolicyId = searchParams.get('selectedPolicy');
  const { data: policies, isLoading } = useQuery({
    ...getAllPoliciesQuery(),
    enabled: !!selectedPolicyId,
  });
  const policyList = Array.isArray(policies) ? policies : [];
  const selectedPolicy =
    policyList.find((p) => p.id.toString() === selectedPolicyId) ?? null;

  const displayPolicy = selectedPolicy;
  const notFound = !!selectedPolicyId && !isLoading && !displayPolicy;

  const [openRunDialog, setOpenRunDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);

  const hasRunPermission = isAbleToSeeUIElement(
    toolsRoles,
    ToolsRoles.MaunalRunPolicy.value
  );
  let runDisabledReason: string | undefined;
  if (hasRunPermission) {
    runDisabledReason =
      selectedPolicy?.policyType === 'newUser'
        ? 'New User Creation policies cannot be manually run.'
        : undefined;
  } else {
    runDisabledReason = Messages.Errors.Permission.Acting(
      ToolsRoles.MaunalRunPolicy.name
    );
  }
  const editDisabledReason = isAbleToSeeUIElement(
    toolsRoles,
    ToolsRoles.EditPolicy.value
  )
    ? undefined
    : Messages.Errors.Permission.Acting(ToolsRoles.EditPolicy.name);
  const cloneDisabledReason = isAbleToSeeUIElement(
    toolsRoles,
    ToolsRoles.CopyPolicy.value
  )
    ? undefined
    : Messages.Errors.Permission.Acting(ToolsRoles.CopyPolicy.name);
  const deleteDisabledReason = isAbleToSeeUIElement(
    toolsRoles,
    ToolsRoles.DeletePolicy.value
  )
    ? undefined
    : Messages.Errors.Permission.Acting(ToolsRoles.DeletePolicy.name);

  const actionsMenu: SimpleDrawerAction[] | undefined = displayPolicy
    ? [
        {
          label: 'Run',
          icon: <PlayArrow fontSize="small" />,
          disabled:
            !isAbleToSeeUIElement(
              toolsRoles,
              ToolsRoles.MaunalRunPolicy.value
            ) ||
            displayPolicy.status === 'RUNNING' ||
            displayPolicy.policyType === 'newUser',
          disabledReason: runDisabledReason,
          onClick: () => {
            setOpenRunDialog(true);
          },
        },
        {
          label: 'Edit',
          icon: <Edit fontSize="small" />,
          disabled: !isAbleToSeeUIElement(
            toolsRoles,
            ToolsRoles.EditPolicy.value
          ),
          disabledReason: editDisabledReason,
          onClick: () => {
            handlePolicyEdit(displayPolicy);
          },
        },
        {
          label: 'Clone',
          icon: <LibraryAdd fontSize="small" />,
          disabled: !isAbleToSeeUIElement(
            toolsRoles,
            ToolsRoles.CopyPolicy.value
          ),
          disabledReason: cloneDisabledReason,
          onClick: () => {
            handlePolicyClone(displayPolicy);
          },
        },
        {
          label: 'Delete',
          icon: <Delete fontSize="small" />,
          disabled: !isAbleToSeeUIElement(
            toolsRoles,
            ToolsRoles.DeletePolicy.value
          ),
          disabledReason: deleteDisabledReason,
          onClick: () => {
            setOpenDeleteDialog(true);
          },
        },
      ]
    : undefined;

  return (
    <>
      <SimpleDrawer
        title="Policy Details"
        open={!!selectedPolicyId}
        onClose={closeDrawer}
        isLoading={isLoading}
        actionsMenu={actionsMenu}
      >
        {displayPolicy && (
          <PolicyDrawerContent
            key={displayPolicy.id}
            selectedPolicy={displayPolicy}
            apiHost={apiUrl}
          />
        )}
        {notFound && (
          <Typography sx={{ p: 3 }} color="error">
            Policy is not found.
          </Typography>
        )}
      </SimpleDrawer>

      {openRunDialog && displayPolicy && (
        <RunPoliciesDialog
          open={openRunDialog}
          handleClose={() => setOpenRunDialog(false)}
          selectedIds={[displayPolicy.id]}
          setSelectedIds={() => {}}
          policyList={[displayPolicy]}
          changeLogRoles={changeLogRoles}
        />
      )}

      {openDeleteDialog && displayPolicy && (
        <DeletePoliciesDialog
          open={openDeleteDialog}
          handleClose={() => setOpenDeleteDialog(false)}
          onDeleteSuccess={closeDrawer}
          changeLogRoles={changeLogRoles}
          selectedPolicies={[displayPolicy.id]}
          setSelectedPolicies={() => {}}
          policyList={[displayPolicy]}
        />
      )}
    </>
  );
}
